import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Save, X, Loader2 } from 'lucide-react';
import { fetchVideos, createVideo, updateVideo, deleteVideo, formatApiError } from '../../services/api';

const emptyVideo = { youtubeId: '', title: '', description: '', duration: '', category: 'Tarot Reading', type: 'video', date: '' };

const extractYouTubeId = (urlOrId) => {
  if (!urlOrId) return '';
  // If already an 11-char ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(urlOrId)) return urlOrId;
  // YouTube URL patterns
  const patterns = [
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = urlOrId.match(p);
    if (m) return m[1];
  }
  return urlOrId;
};

const VideoManager = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVideo);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchVideos();
      setVideos(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing('new');
    setForm(emptyVideo);
    setError('');
  };

  const startEdit = (video) => {
    setEditing(video.id);
    setForm({ ...video });
    setError('');
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyVideo);
    setError('');
  };

  const save = async () => {
    const ytId = extractYouTubeId(form.youtubeId);
    if (!ytId || !form.title || !form.description || !form.category) {
      setError('YouTube URL/ID, Title, Description, and Category are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        youtubeId: ytId,
        title: form.title,
        description: form.description,
        duration: form.duration || '',
        category: form.category,
        type: form.type || 'video',
        date: form.date || undefined,
      };
      if (editing === 'new') await createVideo(payload);
      else await updateVideo(editing, payload);
      await load();
      cancel();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await deleteVideo(id);
      await load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>Videos</h2>
          <p className="text-amber-100/60 text-sm mt-1">{videos.length} video{videos.length !== 1 ? 's' : ''}</p>
        </div>
        {!editing && (
          <Button onClick={startCreate} className="btn-gold" data-testid="new-video-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Video
          </Button>
        )}
      </div>

      {editing && (
        <div className="mb-8 p-6 bg-emerald-950/40 border border-amber-600/30 rounded-lg space-y-4">
          <h3 className="text-xl font-semibold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>
            {editing === 'new' ? 'Add New Video' : 'Edit Video'}
          </h3>

          <div>
            <Label className="text-amber-300 text-xs uppercase tracking-wider">YouTube URL or ID *</Label>
            <Input
              value={form.youtubeId}
              onChange={(e) => setForm({ ...form, youtubeId: e.target.value })}
              placeholder="https://youtube.com/watch?v=... or just the 11-character video ID"
              className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
              data-testid="video-youtube-input"
            />
            {form.youtubeId && extractYouTubeId(form.youtubeId) && (
              <img
                src={`https://img.youtube.com/vi/${extractYouTubeId(form.youtubeId)}/hqdefault.jpg`}
                alt="thumbnail preview"
                className="mt-3 w-48 h-28 object-cover rounded border border-amber-600/30"
              />
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Video title"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
                data-testid="video-title-input"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Category *</Label>
              <Input
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="e.g. Love Reading, Navratri Files"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
                data-testid="video-category-input"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Duration</Label>
              <Input
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                placeholder="e.g. 10:14 or Short"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Type</Label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full px-3 py-2 rounded-md bg-emerald-950/60 border border-amber-600/30 text-amber-100"
              >
                <option value="video">Long Video</option>
                <option value="short">Short</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-amber-300 text-xs uppercase tracking-wider">Description *</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Video description..."
              rows={3}
              className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
              data-testid="video-description-input"
            />
          </div>

          {error && <div className="p-3 rounded bg-red-950/60 border border-red-500/40 text-red-300 text-sm">{error}</div>}

          <div className="flex gap-3">
            <Button onClick={save} disabled={saving} className="btn-gold" data-testid="video-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save Video'}
            </Button>
            <Button onClick={cancel} className="btn-mystical">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-amber-100/60 text-center py-12">Loading videos...</p>
      ) : videos.length === 0 ? (
        <p className="text-amber-100/60 text-center py-12">No videos yet. Click "Add Video" to create one.</p>
      ) : (
        <div className="space-y-3">
          {videos.map((video) => (
            <div
              key={video.id}
              className="flex items-center gap-4 p-4 bg-emerald-950/30 border border-amber-600/20 rounded-lg hover:border-amber-500/50 transition"
              data-testid={`video-row-${video.id}`}
            >
              <img
                src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`}
                alt={video.title}
                className="w-32 h-20 object-cover rounded border border-amber-600/30 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-amber-200 font-semibold truncate" style={{ fontFamily: 'Cinzel, serif' }}>{video.title}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className="bg-emerald-800/80 text-amber-300 border border-amber-500/30 text-xs">{video.category}</Badge>
                  <Badge className="bg-emerald-900/80 text-amber-300/80 border border-amber-500/20 text-xs">{video.type === 'short' ? 'SHORT' : 'VIDEO'}</Badge>
                  <span className="text-xs text-amber-100/50">{video.duration}</span>
                </div>
                <p className="text-sm text-amber-100/70 line-clamp-1 mt-1">{video.description}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" onClick={() => startEdit(video)} className="btn-mystical" data-testid={`edit-video-${video.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" onClick={() => handleDelete(video.id)} className="bg-red-900/60 text-red-300 hover:bg-red-900 border border-red-500/30" data-testid={`delete-video-${video.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoManager;
